import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Typography } from '@/components/ui/typography'
import {
  AdminUserStatus,
  type AdminUserStatusKind,
} from '@/components/custom/admin/admin-user-status'
import { cn } from '@/lib/utils'

interface AdminDirectoryRowBase {
  id: string
  name: string
  email: string
  initials: string
  role: string
  appAccess: string
  /** Second line under App access (e.g. "OGSM: Read only"). */
  appAccessDetail?: string
  companies?: string
  status: {
    kind: AdminUserStatusKind
    label: string
    detail?: string
  }
}

export type AdminDirectoryRow = AdminDirectoryRowBase &
  (
    | {
        onActivate: () => void
        activationLabel: string
        actions?: never
      }
    | {
        onActivate?: never
        activationLabel?: never
        actions: React.ReactNode
      }
  )

interface AdminUserTableProps {
  rows: AdminDirectoryRow[]
  showCompanies?: boolean
  emptyMessage: string
  ariaLabel: string
}

const AVATAR_STYLES = [
  'bg-blue-100 text-blue-800',
  'bg-orange-100 text-orange-800',
  'bg-emerald-100 text-emerald-800',
  'bg-violet-100 text-violet-800',
  'bg-amber-100 text-amber-800',
  'bg-slate-100 text-slate-700',
]

export function AdminUserTable({
  rows,
  showCompanies = false,
  emptyMessage,
  ariaLabel,
}: AdminUserTableProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-primary/15 px-5 py-14 text-center">
        <Typography variant="body-sm" className="text-primary/70">
          {emptyMessage}
        </Typography>
      </div>
    )
  }

  const desktopColumns = showCompanies
    ? '@[74rem]/internal-users:grid-cols-[minmax(230px,1.55fr)_130px_150px_minmax(160px,1.2fr)_130px_150px]'
    : '@[55rem]/portco-users:grid-cols-[minmax(220px,1.5fr)_120px_145px_135px_150px]'

  return (
    <div
      role="table"
      aria-label={ariaLabel}
      className={cn(
        'min-w-0',
        showCompanies ? '@container/internal-users' : '@container/portco-users'
      )}
    >
      <div
        role="row"
        className={cn(
          'hidden gap-5 border-b border-primary/15 px-3 py-3 @[74rem]/internal-users:grid @[55rem]/portco-users:grid',
          desktopColumns
        )}
      >
        <HeaderCell>User</HeaderCell>
        <HeaderCell>Role</HeaderCell>
        <HeaderCell>App access</HeaderCell>
        {showCompanies ? <HeaderCell>Companies</HeaderCell> : null}
        <HeaderCell>Status</HeaderCell>
        <HeaderCell>Actions</HeaderCell>
      </div>

      {rows.map((row, index) => (
        <article
          role="row"
          key={row.id}
          className={cn(
            'relative isolate grid gap-4 px-3 py-4 @[74rem]/internal-users:items-center @[55rem]/portco-users:items-center @[74rem]/internal-users:gap-5 @[55rem]/portco-users:gap-5',
            desktopColumns,
            row.onActivate &&
              'transition-colors hover:bg-primary/[0.035] focus-within:bg-primary/[0.05]',
            index < rows.length - 1 && 'border-b border-primary/10'
          )}
        >
          <div role="cell" className="flex min-w-0 items-center gap-3">
            {row.onActivate ? (
              <button
                type="button"
                aria-label={row.activationLabel}
                onClick={row.onActivate}
                className="absolute inset-0 z-20 cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              />
            ) : null}
            <Avatar className="size-10">
              <AvatarFallback
                className={cn(
                  'text-sm font-medium',
                  AVATAR_STYLES[avatarIndex(row.id)]
                )}
              >
                {row.initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <Typography variant="body" className="truncate text-primary">
                {row.name}
              </Typography>
              <Typography
                variant="body-sm"
                className="truncate italic text-primary/70"
              >
                {row.email}
              </Typography>
            </div>
          </div>

          <CompactField label="Role" value={row.role} />
          <CompactField
            label="App access"
            value={row.appAccess}
            detail={row.appAccessDetail}
          />
          {showCompanies ? (
            <CompactField label="Companies" value={row.companies ?? 'None'} />
          ) : null}
          <div
            role="cell"
            className="grid grid-cols-[96px_1fr] gap-3 @[74rem]/internal-users:block @[55rem]/portco-users:block"
          >
            <Typography
              as="span"
              variant="body-sm"
              className="font-medium uppercase tracking-wide text-primary/70 @[74rem]/internal-users:hidden @[55rem]/portco-users:hidden"
            >
              Status
            </Typography>
            <AdminUserStatus {...row.status} />
          </div>
          {row.actions ? (
            <div
              role="cell"
              className="grid grid-cols-[96px_1fr] gap-3 @[74rem]/internal-users:flex @[55rem]/portco-users:flex @[74rem]/internal-users:flex-wrap @[55rem]/portco-users:flex-wrap @[74rem]/internal-users:items-center @[55rem]/portco-users:items-center @[74rem]/internal-users:gap-2 @[55rem]/portco-users:gap-2"
            >
              <Typography
                as="span"
                variant="body-sm"
                className="font-medium uppercase tracking-wide text-primary/70 @[74rem]/internal-users:hidden @[55rem]/portco-users:hidden"
              >
                Actions
              </Typography>
              <div className="flex flex-wrap items-center gap-2">
                {row.actions}
              </div>
            </div>
          ) : (
            <div
              role="cell"
              className="hidden @[74rem]/internal-users:block @[55rem]/portco-users:block"
            />
          )}
        </article>
      ))}
    </div>
  )
}

function HeaderCell({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      role="columnheader"
      variant="body-sm"
      className="font-semibold uppercase tracking-wide text-primary/70"
    >
      {children}
    </Typography>
  )
}

function CompactField({
  label,
  value,
  detail,
}: {
  label: string
  value: string
  detail?: string
}) {
  return (
    <div
      role="cell"
      className="grid min-w-0 grid-cols-[96px_1fr] gap-3 @[74rem]/internal-users:block @[55rem]/portco-users:block"
    >
      <Typography
        as="span"
        variant="body-sm"
        className="font-medium uppercase tracking-wide text-primary/70 @[74rem]/internal-users:hidden @[55rem]/portco-users:hidden"
      >
        {label}
      </Typography>
      <div className="min-w-0">
        <Typography
          as="span"
          variant="body-sm"
          className="break-words text-primary/85"
        >
          {value}
        </Typography>
        {detail ? (
          <Typography
            as="span"
            variant="body-sm"
            className="block break-words text-primary/70"
          >
            {detail}
          </Typography>
        ) : null}
      </div>
    </div>
  )
}

function avatarIndex(value: string) {
  return (
    Array.from(value).reduce(
      (sum, character) => sum + character.charCodeAt(0),
      0
    ) % AVATAR_STYLES.length
  )
}
